import type { LibraryDetail, LibraryRevision } from '../documents/types.js';
import type { NewRecordInput } from './NewRecordDialog.js';
import { attachFilledPdf, recordFileName } from './pdf-form.js';
import { printRenditionOf, revisionSourceOf } from './record-model.js';
import type { ShipFormActions } from './source.js';
import type { FormRecordDetail, FormTemplate } from './types.js';

/**
 * Form List'teki kayıt yolları (ADR 0042 §3, §18–§20; plan M8b Task 18). Her yol taslak açar
 * (kayıt numarası ayrılır) ve açılan kaydın kimliğini döner; ekran kayıt sayfasına gider. Saf
 * akışlardır: kaynak ve ana süreç dışarıdan verilir (test).
 */

interface FlowBase {
  ship: ShipFormActions;
  detail: Pick<LibraryDetail, 'id' | 'owner'>;
  revision: Pick<LibraryRevision, 'id' | 'file' | 'attachments'>;
}

export type OpenFormOutcome =
  | { kind: 'opened'; record: FormRecordDetail }
  /**
   * İlişkili uygulama yok (ADR 0024 #20): taslak gerekçeyle bırakıldı (numara "Discarded"
   * olarak görünür kalır; Ek U48); ekran diğer yolları gösterir.
   */
  | { kind: 'no_app'; recordNo: string; message: string }
  | { kind: 'failed'; record: FormRecordDetail; code: string };

/**
 * Open Form (§18): taslak (`office`) → çalışma kopyası şablondan → ilişkili uygulamada açılır.
 * Uygulama yoksa taslak gerekçeyle bırakılır ve çalışma klasörü kaldırılır.
 */
export async function openFormFlow(
  input: FlowBase & { template: FormTemplate; discardReason: string },
): Promise<OpenFormOutcome> {
  const work = input.ship.work;
  if (!work) throw Object.assign(new Error('OPEN_FAILED'), { code: 'OPEN_FAILED' });
  const record = await input.ship.open({
    documentId: input.detail.id,
    revisionSource: revisionSourceOf(input.detail),
    fillMethod: 'office',
  });
  const result = await work.openInOffice({
    recordId: record.id,
    file: {
      kind: 'template',
      fileId: input.revision.file.id,
      sha256: input.template.source.sha256,
      name: recordFileName(record.recordNo, input.revision.file.name),
    },
  });
  if (result.ok) return { kind: 'opened', record };
  if (result.code === 'NO_ASSOCIATED_APP') {
    await input.ship.discard(record.id, { reason: input.discardReason }).catch(() => undefined);
    await work.release(record.id).catch(() => undefined);
    return { kind: 'no_app', recordNo: record.recordNo, message: result.message ?? '' };
  }
  return { kind: 'failed', record, code: result.code };
}

/**
 * Print Form (§20): onay penceresinden sonra taslak (`print_scan`) açılır ve şablon basılır;
 * altlıkta kayıt no. Revizyonun onaylı baskı PDF'i varsa o basılır (ADR 0028 #34(3)); kaydın
 * `template_role`'ü ve `template_sha256`'sı sunucuda basılan dosyanınkidir (Ek U14). Basılamazsa
 * taslak kalır.
 */
export async function printFormFlow(
  input: FlowBase & {
    fileUrl: (fileId: string) => string;
    print: (options: { recordNo: string; renditionUrl: string | null }) => Promise<void>;
  },
): Promise<{ record: FormRecordDetail; printError: unknown }> {
  const record = await input.ship.open({
    documentId: input.detail.id,
    revisionSource: revisionSourceOf(input.detail),
    fillMethod: 'print_scan',
  });
  const rendition = printRenditionOf(input.revision);
  try {
    await input.print({
      recordNo: record.recordNo,
      renditionUrl: rendition ? input.fileUrl(rendition.id) : null,
    });
    return { record, printError: null };
  } catch (error) {
    return { record, printError: error };
  }
}

/** New record (§3): yürürlükteki revizyon ya da gerekçeli kâğıt istisnası. */
export function newRecordFlow(
  input: Pick<FlowBase, 'ship' | 'detail'> & { values: NewRecordInput },
): Promise<FormRecordDetail> {
  return input.ship.open({
    documentId: input.detail.id,
    revisionSource: revisionSourceOf(input.detail),
    fillMethod: 'print_scan',
    ...(input.values.revisionId ? { revisionId: input.values.revisionId } : {}),
    ...(input.values.paperRevisionReason
      ? { paperRevisionReason: input.values.paperRevisionReason }
      : {}),
    title: input.values.title,
  });
}

/**
 * Fill in app (§19): numara yalnız kaydedilince ayrılır (vazgeçilen doldurma kayıt açmaz);
 * taslak (`pdf_form`) açılır, doldurulan PDF `pdf_form` kaynağıyla eklenir. Açılan taslak
 * `session`'da tutulur: yükleme yarıda kalırsa yeniden deneme aynı taslağa ekler, ikinci kayıt
 * numarası açılmaz; pencere kapanırsa ekran kullanıcıyı o taslağa götürür [sorular #522].
 */
export async function fillInAppFlow(
  input: FlowBase & {
    bytes: Uint8Array;
    session?: { draft: FormRecordDetail | null };
  },
): Promise<FormRecordDetail> {
  const work = input.ship.work;
  if (!work) throw Object.assign(new Error('OPEN_FAILED'), { code: 'OPEN_FAILED' });
  const record =
    input.session?.draft ??
    (await input.ship.open({
      documentId: input.detail.id,
      revisionSource: revisionSourceOf(input.detail),
      fillMethod: 'pdf_form',
    }));
  if (input.session) input.session.draft = record;
  return attachFilledPdf({
    record,
    bytes: input.bytes,
    templateName: input.revision.file.name,
    upload: (name, data) => work.uploadBytes(name, data),
    updateDraft: (id, patch) => input.ship.updateDraft(id, patch),
  });
}
